import posthog from 'posthog-js';

import { logger } from '../lib/logger';

const log = logger.child({ module: 'utm' });

const STORAGE_KEY = 'floto.utm';
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

/**
 * The campaign a visitor arrived from, kept from their first landing.
 *
 * New users reach the app from a link on floto.ai (`/?url=…&utm_source=…`),
 * and the tags have to survive what follows: the redirect to /login, a Google
 * popup, a magic-link email (often opened in another tab), and onboarding,
 * where they are written to the Floto profile — same `utm` field flotoapps
 * sends. localStorage rather than sessionStorage for exactly that new tab.
 *
 * First touch wins: a later visit with different tags does not overwrite the
 * campaign that brought them in.
 */
export function captureUtm(search: string): void {
  const params = new URLSearchParams(search);
  captureSrc(params);
  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) utm[key] = value.slice(0, 200);
  }
  if (Object.keys(utm).length === 0) return;

  try {
    if (!localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
    }
  } catch (err) {
    log.warn({ err }, 'could not store utm tags');
  }

  // PostHog already reads utm_* off the landing pageview; registering them
  // as once-only super-properties puts the campaign on every later event too
  // (sign-in, onboarding, the first session), which is the funnel that matters.
  try {
    if (posthog.__loaded) posthog.register_once(utm);
  } catch (err) {
    log.warn({ err }, 'could not register utm tags');
  }
}

/**
 * `?src=` on a link we sent ourselves, e.g. `mobile_signup_email` on the
 * email asking a phone signup to come back on a desktop. Not a campaign, so
 * it stays out of the profile's `utm`; registered once on PostHog, it rides
 * on every later event, past the /login redirect and through sign-in.
 */
function captureSrc(params: URLSearchParams): void {
  const src = params.get('src')?.trim();
  if (!src) return;
  try {
    if (posthog.__loaded) posthog.register_once({ src: src.slice(0, 200) });
  } catch (err) {
    log.warn({ err }, 'could not register src');
  }
}

/** The stored first-touch tags, or undefined if they never came with any. */
export function readUtm(): Record<string, string> | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return undefined;
    const utm = Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string',
      ),
    );
    return Object.keys(utm).length > 0 ? utm : undefined;
  } catch {
    return undefined;
  }
}
